import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, ExternalLink, X, Check, Megaphone } from 'lucide-react'
import { api } from '@/util/api'
import { Badge, Button, ButtonLink, Card, Checkbox, ConfirmDialog, EmptyState, IconButton, Notice, Switch, TextField, useToast } from '@/components/play'
import { workspaceError } from '@/lib/workspace'
import { cn } from '@/lib/utils'
import type {
  CampaignAttributes,
  CampaignResource,
  CampaignsIndexResponse,
  CampaignResponse,
} from '@/types/events'

type SponsorsSectionProps = {
  eventId: string
  /**
   * Called when campaigns or their active state change so the parent page
   * can refresh anywhere `streams.ad_tag` is shown alongside sponsor
   * autocomplete suggestions.
   */
  onCampaignsChanged?: (campaigns: CampaignResource[]) => void
}

export function SponsorsSection({ eventId, onCampaignsChanged }: SponsorsSectionProps) {
  const [campaigns, setCampaigns] = useState<CampaignResource[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<CampaignResource | null>(null)
  const [creating, setCreating] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [deleteError, setDeleteError] = useState('')
  const { toast } = useToast()

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await api.get<CampaignsIndexResponse>(`/events/${eventId}/campaigns`)
      setCampaigns(res.data)
      onCampaignsChanged?.(res.data)
    } catch (e: unknown) {
      setLoadError(workspaceError(e, 'Your sponsor campaigns couldn’t load. Try again.'))
    } finally {
      setLoading(false)
    }
  }, [eventId, onCampaignsChanged])

  useEffect(() => {
    void load()
  }, [load])

  const handleSaved = (saved: CampaignResource) => {
    setCampaigns((prev) => {
      const next = prev.some((c) => c.id === saved.id)
        ? prev.map((c) => (c.id === saved.id ? saved : c))
        : [saved, ...prev]
      onCampaignsChanged?.(next)
      return next
    })
    setEditing(null)
    setCreating(false)
  }

  // Confirmed through ConfirmDialog, which stays loading until this settles and keeps a failure in view.
  const handleDelete = async (campaign: CampaignResource) => {
    setDeleteError('')
    try {
      await api.delete(`/events/${eventId}/campaigns/${campaign.id}`)
    } catch (e: unknown) {
      setDeleteError(workspaceError(e, 'The campaign couldn’t be deleted. Try again.'))
      throw e
    }
    setCampaigns((prev) => {
      const next = prev.filter((c) => c.id !== campaign.id)
      onCampaignsChanged?.(next)
      return next
    })
    toast({ title: 'Campaign deleted', tone: 'success' })
  }

  const handleToggleActive = async (campaign: CampaignResource) => {
    const form = new FormData()
    form.append('active', String(!campaign.attributes.active))
    try {
      const res = await api.patchMultipart<CampaignResponse>(
        `/events/${eventId}/campaigns/${campaign.id}`,
        form,
      )
      handleSaved(res.data)
    } catch (e: unknown) {
      toast({
        title: 'Campaign not updated',
        description: workspaceError(e, 'The campaign couldn’t be turned on or off. Try again.'),
        tone: 'error',
      })
    }
  }

  return (
    <Card
      title="Sponsors"
      description="Cameras inherit a sponsor when their ad tag matches an active campaign."
      actions={
        !creating && !editing && (
          <Button size="compact" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" /> Add campaign
          </Button>
        )
      }
    >
      {(creating || editing) && (
        <CampaignForm
          eventId={eventId}
          campaign={editing}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={handleSaved}
        />
      )}

      {loading && <ContentSkeleton label="Loading sponsor campaigns" variant="list" />}
      {!loading && loadError && (
        <Notice
          tone="error"
          action={<Button variant="secondary" size="compact" onClick={() => void load()}>Try again</Button>}
        >
          {loadError}
        </Notice>
      )}
      {!loading && !loadError && campaigns.length === 0 && !creating && (
        <EmptyState
          size="compact"
          headingLevel={3}
          icon={<Megaphone />}
          title="No campaigns yet"
          description="Add one to start sponsoring cameras by role tag."
        />
      )}
      {!loading && campaigns.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onEdit={() => setEditing(c)}
              onDelete={() => handleDelete(c)}
              onConfirmDelete={() => setDeleteError('')}
              deleteError={deleteError}
              onToggleActive={() => handleToggleActive(c)}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

function CampaignCard({
  campaign,
  onEdit,
  onDelete,
  onConfirmDelete,
  deleteError,
  onToggleActive,
}: {
  campaign: CampaignResource
  onEdit: () => void
  onDelete: () => Promise<void>
  onConfirmDelete: () => void
  deleteError: string
  onToggleActive: () => void
}) {
  const a = campaign.attributes
  return (
    // A paused campaign keeps full contrast; its switch says it is off.
    <div className="flex flex-col gap-s rounded-2xl bg-neutral-1 p-m">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-neutral-10">{a.sponsor_name}</div>
          <div className="truncate text-xs text-neutral-7">{a.campaign_name}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IconButton variant="ghost" size="compact" label={`Edit ${a.sponsor_name}`} onClick={onEdit}>
            <Pencil />
          </IconButton>
          <ConfirmDialog
            trigger={
              <IconButton variant="ghost" size="compact" label={`Delete ${a.sponsor_name}`} onClick={onConfirmDelete}>
                <Trash2 />
              </IconButton>
            }
            title={`Delete the ${a.sponsor_name} campaign?`}
            description={`Cameras tagged ${a.tag} stop inheriting this sponsor.`}
            confirmLabel="Delete campaign"
            onConfirm={onDelete}
            failureMessage={deleteError || undefined}
          />
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-neutral-10 px-3 py-2">
        {a.logo_url ? (
          <img
            src={a.logo_url}
            alt={a.sponsor_name}
            className="block max-h-8 max-w-[120px] object-contain"
          />
        ) : (
          <span className="text-xs text-neutral-5">No logo uploaded</span>
        )}
        <Badge className="ml-auto">{a.tag}</Badge>
      </div>

      <div className="flex items-center justify-between gap-s">
        <ButtonLink
          variant="ghost"
          size="compact"
          href={a.click_through_url}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0"
        >
          <ExternalLink aria-hidden="true" />
          <span className="truncate">{hostFromUrl(a.click_through_url)}</span>
        </ButtonLink>
        <Switch
          // The sponsor's name is read with Active, so each card's switch has its own name.
          label={<><span className="sr-only">{`${a.sponsor_name} campaign `}</span>Active</>}
          checked={a.active}
          onCheckedChange={onToggleActive}
        />
      </div>
    </div>
  )
}

function CampaignForm({
  eventId,
  campaign,
  onCancel,
  onSaved,
}: {
  eventId: string
  campaign: CampaignResource | null
  onCancel: () => void
  onSaved: (saved: CampaignResource) => void
}) {
  const existing = campaign?.attributes
  const [sponsorName, setSponsorName] = useState(existing?.sponsor_name ?? '')
  const [campaignName, setCampaignName] = useState(existing?.campaign_name ?? '')
  const [tag, setTag] = useState(existing?.tag ?? '')
  const [clickThroughUrl, setClickThroughUrl] = useState(existing?.click_through_url ?? '')
  const [altText, setAltText] = useState(existing?.alt_text ?? '')
  const [active, setActive] = useState(existing?.active ?? true)
  const [logo, setLogo] = useState<File | null>(null)
  const [bannerMobile, setBannerMobile] = useState<File | null>(null)
  const [bannerPillar, setBannerPillar] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { toast } = useToast()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const form = new FormData()
      form.append('sponsor_name', sponsorName)
      form.append('campaign_name', campaignName)
      form.append('tag', tag)
      form.append('click_through_url', clickThroughUrl)
      form.append('alt_text', altText)
      form.append('active', String(active))
      if (logo) form.append('logo', logo)
      if (bannerMobile) form.append('banner_mobile', bannerMobile)
      if (bannerPillar) form.append('banner_pillar', bannerPillar)

      const res = campaign
        ? await api.patchMultipart<CampaignResponse>(
            `/events/${eventId}/campaigns/${campaign.id}`,
            form,
          )
        : await api.postMultipart<CampaignResponse>(`/events/${eventId}/campaigns`, form)
      onSaved(res.data)
      toast({ title: campaign ? 'Campaign updated' : 'Campaign created', tone: 'success' })
    } catch (e: unknown) {
      toast({
        title: 'Campaign not saved',
        description: workspaceError(e, 'The campaign couldn’t be saved. Try again.'),
        tone: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} className="mb-l space-y-m rounded-2xl bg-neutral-1 p-l">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextField
          label="Sponsor name"
          id="sponsor-name"
          value={sponsorName}
          onChange={(e) => setSponsorName(e.target.value)}
          placeholder="Cell C"
          required
        />
        <TextField
          label="Campaign name"
          id="campaign-name"
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          placeholder="Comrades Bundles"
          required
        />
        <TextField
          label="Ad tag"
          description="Cameras tagged with this string inherit this sponsor."
          id="campaign-tag"
          value={tag}
          onChange={(e) => setTag(e.target.value.toLowerCase())}
          placeholder="men_lead"
          pattern="[a-zA-Z0-9_\-]+"
          required
        />
        <TextField
          label="Click-through URL"
          id="campaign-url"
          type="url"
          value={clickThroughUrl}
          onChange={(e) => setClickThroughUrl(e.target.value)}
          placeholder="https://"
          required
        />
        <TextField
          label="Tagline / alt text"
          optional
          id="campaign-alt"
          className="md:col-span-2"
          value={altText ?? ''}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="Run your own race"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <FileField
          label="Logo"
          accept="image/png,image/jpeg,image/webp"
          existingUrl={existing?.logo_url}
          file={logo}
          onChange={setLogo}
          hint="Map marker lockup · PNG/JPG, transparent preferred · ~480×160 (3:1)"
        />
        <FileField
          label="Banner — mobile"
          accept="image/png,image/jpeg,image/webp"
          existingUrl={existing?.banner_mobile_url}
          file={bannerMobile}
          onChange={setBannerMobile}
          hint="Horizontal · portrait immersive flush bottom · ~1280×200 (16:2.5)"
        />
        <FileField
          label="Pillar — immersive"
          accept="image/png,image/jpeg,image/webp"
          existingUrl={existing?.banner_pillar_url}
          file={bannerPillar}
          onChange={setBannerPillar}
          hint="Portrait creative beside the video · 9:16 (1080×1920) · keep key copy centred — sides crop slightly"
          portrait
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-s pt-xs">
        <Checkbox
          label="Active"
          description="Cameras with this tag show the sponsor."
          checked={active}
          onCheckedChange={(checked) => setActive(checked)}
        />
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="compact" onClick={onCancel}>
            <X aria-hidden="true" /> Cancel
          </Button>
          <Button type="submit" size="compact" loading={submitting}>
            <Check aria-hidden="true" />
            {submitting ? 'Saving' : campaign ? 'Save changes' : 'Create campaign'}
          </Button>
        </div>
      </div>
    </form>
  )
}

/** An image for the campaign: a Play file field with its spec as the description, and a preview of the new or current
 *  image below it. */
function FileField({
  label,
  accept,
  existingUrl,
  file,
  onChange,
  hint,
  portrait = false,
}: {
  label: string
  accept: string
  existingUrl?: string | null
  file: File | null
  onChange: (file: File | null) => void
  hint?: string
  /** The pillar's 9:16 preview; others are a short landscape strip. */
  portrait?: boolean
}) {
  const [inputKey, setInputKey] = useState(0)
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : existingUrl), [file, existingUrl])
  useEffect(() => () => {
    if (file && previewUrl) URL.revokeObjectURL(previewUrl)
  }, [file, previewUrl])
  return (
    <div className="flex flex-col gap-xs">
      <TextField
        key={inputKey}
        label={label}
        description={hint}
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {previewUrl && (
        <div
          className={cn(
            'flex items-center justify-center overflow-hidden rounded-xl bg-neutral-3',
            portrait ? 'mx-auto aspect-[9/16] w-24' : 'h-20 px-s',
          )}
        >
          <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
      {file && (
        <Button
          variant="ghost"
          size="compact"
          className="self-start"
          onClick={() => {
            onChange(null)
            setInputKey((key) => key + 1)
          }}
        >
          Remove
        </Button>
      )}
    </div>
  )
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return url
  }
}
